import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import pogMarker from "../../../../assets/casino/pog.png";
import {
  ROULETTE_COLUMNS,
  ROULETTE_DOZENS,
  ROULETTE_NUMBERS,
  formatPogs,
  getStraightColour,
  rouletteRegionKey,
} from "./rouletteConstants";

const ROULETTE_RIGHT_SIDE_BETS = [
  { type: "range", selection: "1-18", label: "1-18", variant: "outside" },
  { type: "parity", selection: "even", label: "EVEN", variant: "outside" },
  { type: "colour", selection: "red", label: "RED", variant: "red" },
  { type: "colour", selection: "black", label: "BLACK", variant: "black" },
  { type: "parity", selection: "odd", label: "ODD", variant: "outside" },
  { type: "range", selection: "19-36", label: "19-36", variant: "outside" },
];

const ROULETTE_INTERSECTION_BETS = [
  {
    type: "trio",
    selection: "0_1_2",
    label: "0 / 1 / 2",
    seam: "left",
    boundary: 0,
  },
  {
    type: "trio",
    selection: "00_2_3",
    label: "00 / 2 / 3",
    seam: "right",
    boundary: 0,
  },
  ...Array.from({ length: 11 }, (_, rowIndex) => {
    const firstNumber = rowIndex * 3 + 1;
    const boundary = rowIndex + 1;

    return [
      {
        type: "corner",
        selection: [
          firstNumber,
          firstNumber + 1,
          firstNumber + 3,
          firstNumber + 4,
        ].join("_"),
        label: [
          firstNumber,
          firstNumber + 1,
          firstNumber + 3,
          firstNumber + 4,
        ].join(" / "),
        seam: "left",
        boundary,
      },
      {
        type: "corner",
        selection: [
          firstNumber + 1,
          firstNumber + 2,
          firstNumber + 4,
          firstNumber + 5,
        ].join("_"),
        label: [
          firstNumber + 1,
          firstNumber + 2,
          firstNumber + 4,
          firstNumber + 5,
        ].join(" / "),
        seam: "right",
        boundary,
      },
    ];
  }).flat(),
];

function getIntersectionLeft(seam) {
  return seam === "left"
    ? "calc(33.333333% - 1px)"
    : "calc(66.666667% + 1px)";
}

function getIntersectionTop(boundary) {
  if (boundary === 0) {
    return "-3px";
  }

  const percentage = (boundary / 12) * 100;
  const gapOffset = boundary / 2 - 3;

  if (gapOffset === 0) {
    return `${percentage.toFixed(6)}%`;
  }

  const operator = gapOffset > 0 ? "+" : "-";

  return `calc(${percentage.toFixed(6)}% ${operator} ${Math.abs(
    gapOffset
  )}px)`;
}

function RouletteBetMarkers({ bets }) {
  if (!bets?.length) {
    return null;
  }

  const totalAmount = bets.reduce(
    (total, bet) => total + Number(bet?.amount || 0),
    0
  );

  return (
    <span
      className="roulette-region-markers"
      aria-label={`${formatPogs(totalAmount)} POGS total`}
    >
      <span
        className="roulette-pog-marker"
        title={`${formatPogs(totalAmount)} POGS total`}
      >
        <span className="roulette-pog-marker-stack">
          <span className="roulette-pog-marker-icon">
            <img src={pogMarker} alt="" aria-hidden="true" />
          </span>
          <span className="roulette-pog-marker-total">
            {formatPogs(totalAmount)}
          </span>
        </span>
      </span>
    </span>
  );
}

function RouletteBetRegion({
  type,
  selection,
  label,
  variant,
  bets,
  readOnly,
  onSelect,
  onBetPointerEnter,
  onBetPointerMove,
  onBetPointerLeave,
}) {
  const className = [
    "roulette-bet-region",
    variant ? `roulette-bet-region-${variant}` : "",
    readOnly ? "roulette-bet-region-readonly" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={className}
      onClick={() => onSelect({ type, selection, label })}
      onPointerEnter={readOnly ? undefined : onBetPointerEnter}
      onPointerMove={readOnly ? undefined : onBetPointerMove}
      onPointerLeave={readOnly ? undefined : onBetPointerLeave}
      onPointerCancel={readOnly ? undefined : onBetPointerLeave}
      disabled={readOnly}
      aria-label={`${label} roulette bet${readOnly ? ", locked" : ""}`}
    >
      <span className="roulette-region-label">{label}</span>
      <RouletteBetMarkers bets={bets} />
    </button>
  );
}

function RouletteIntersectionHotspot({
  region,
  bets,
  readOnly,
  selected,
  onSelect,
  onBetPointerEnter,
  onBetPointerMove,
  onBetPointerLeave,
}) {
  const className = [
    "roulette-intersection-hotspot",
    selected ? "roulette-intersection-hotspot-selected" : "",
    readOnly ? "roulette-intersection-hotspot-readonly" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={className}
      style={{
        left: getIntersectionLeft(region.seam),
        top: getIntersectionTop(region.boundary),
      }}
      onClick={() =>
        onSelect({
          type: region.type,
          selection: region.selection,
          label: region.label,
        })
      }
      onPointerEnter={readOnly ? undefined : onBetPointerEnter}
      onPointerMove={readOnly ? undefined : onBetPointerMove}
      onPointerLeave={readOnly ? undefined : onBetPointerLeave}
      onPointerCancel={readOnly ? undefined : onBetPointerLeave}
      disabled={readOnly}
      aria-label={`${region.label} ${region.type} roulette bet${
        readOnly ? ", locked" : ""
      }`}
      aria-pressed={selected}
    >
      <span className="roulette-intersection-hit-outline" aria-hidden="true" />
      <RouletteBetMarkers bets={bets} />
    </button>
  );
}

function RouletteBoard({
  bets,
  readOnly,
  selectedRegion,
  onSelectRegion,
}) {
  const [pogCursor, setPogCursor] = useState({
    visible: false,
    x: 0,
    y: 0,
  });

  const movePogCursor = (event) => {
    setPogCursor({
      visible: true,
      x: event.clientX,
      y: event.clientY,
    });
  };

  const hidePogCursor = () => {
    setPogCursor((current) =>
      current.visible
        ? { ...current, visible: false }
        : current
    );
  };

  const betsByRegion = useMemo(() => {
    const grouped = new Map();

    (bets || []).forEach((bet) => {
      const key = rouletteRegionKey(bet.type, bet.selection);
      const current = grouped.get(key) || [];
      current.push(bet);
      grouped.set(key, current);
    });

    return grouped;
  }, [bets]);

  const regionBets = (type, selection) =>
    betsByRegion.get(rouletteRegionKey(type, selection)) || [];

  const selectedRegionKey = selectedRegion
    ? rouletteRegionKey(selectedRegion.type, selectedRegion.selection)
    : null;

  const betPointerProps = {
    onBetPointerEnter: movePogCursor,
    onBetPointerMove: movePogCursor,
    onBetPointerLeave: hidePogCursor,
  };

  return (
    <div className="roulette-board" aria-label="American roulette betting board">
      <div className="roulette-zero-row-layout">
        <div aria-hidden="true" />
        <div className="roulette-zero-grid">
          {["0", "00"].map((selection) => (
            <RouletteBetRegion
              key={selection}
              type="straight"
              selection={selection}
              label={selection}
              variant="green"
              bets={regionBets("straight", selection)}
              readOnly={readOnly}
              onSelect={onSelectRegion}
              {...betPointerProps}
            />
          ))}
        </div>
        <div aria-hidden="true" />
      </div>

      <div className="roulette-number-dozen-layout">
        <div className="roulette-dozen-rail">
          {ROULETTE_DOZENS.map((region) => (
            <RouletteBetRegion
              key={region.selection}
              {...region}
              variant="outside"
              bets={regionBets(region.type, region.selection)}
              readOnly={readOnly}
              onSelect={onSelectRegion}
              {...betPointerProps}
            />
          ))}
        </div>

        <div className="roulette-number-grid-wrap">
          <div className="roulette-number-grid">
            {ROULETTE_NUMBERS.map((selection) => (
              <RouletteBetRegion
                key={selection}
                type="straight"
                selection={selection}
                label={selection}
                variant={getStraightColour(selection)}
                bets={regionBets("straight", selection)}
                readOnly={readOnly}
                onSelect={onSelectRegion}
                {...betPointerProps}
              />
            ))}
          </div>

          <div
            className="roulette-intersection-layer"
            aria-label="Roulette intersection bets"
          >
            {ROULETTE_INTERSECTION_BETS.map((region) => {
              const key = rouletteRegionKey(region.type, region.selection);

              return (
                <RouletteIntersectionHotspot
                  key={key}
                  region={region}
                  bets={regionBets(region.type, region.selection)}
                  readOnly={readOnly}
                  selected={selectedRegionKey === key}
                  onSelect={onSelectRegion}
                  {...betPointerProps}
                />
              );
            })}
          </div>
        </div>

        <div className="roulette-right-rail">
          {ROULETTE_RIGHT_SIDE_BETS.map((region) => (
            <RouletteBetRegion
              key={`${region.type}-${region.selection}`}
              {...region}
              bets={regionBets(region.type, region.selection)}
              readOnly={readOnly}
              onSelect={onSelectRegion}
              {...betPointerProps}
            />
          ))}
        </div>
      </div>

      <div className="roulette-three-grid">
        {ROULETTE_COLUMNS.map((region) => (
          <RouletteBetRegion
            key={region.selection}
            {...region}
            variant="outside"
            bets={regionBets(region.type, region.selection)}
            readOnly={readOnly}
            onSelect={onSelectRegion}
            {...betPointerProps}
          />
        ))}
      </div>

      {pogCursor.visible &&
        createPortal(
          <img
            className="roulette-desktop-pog-cursor"
            src={pogMarker}
            alt=""
            aria-hidden="true"
            draggable="false"
            style={{
              left: `${pogCursor.x}px`,
              top: `${pogCursor.y}px`,
            }}
          />,
          document.body
        )}
    </div>
  );
}

export default RouletteBoard;
